import type { Platform } from '@rocco/ui/social-sharing/utils/platform';
import { toast } from 'sonner';

/**
 * Options for sharing content on social platforms.
 */
export interface ShareOptions {
    /** The URL to be shared */
    url: string;
    /** Optional title of the content being shared */
    title?: string;
    /** Optional description of the content being shared */
    description?: string;
}

/**
 * Result of a sharing operation
 */
interface ShareResult {
    /** Whether the sharing operation was successful */
    success: boolean;
    /** Optional error message if sharing failed */
    error?: string;
    /** The platform that was used for sharing, undefined if native sharing was used */
    platform?: Platform;
}

/**
 * Copies text to clipboard and shows a success toast
 *
 * @param text - The text to copy to clipboard
 * @returns Promise resolving to a boolean indicating success
 */
export function copyToClipboard(text: string): Promise<boolean> {
    if (!navigator.clipboard) {
        toast.error('Could not copy to clipboard');
        return Promise.resolve(false);
    }

    return navigator.clipboard
        .writeText(text)
        .then(() => {
            toast.success('Link copied to clipboard', {
                action: {
                    label: 'Dismiss',
                    onClick: () => toast.dismiss(),
                },
            });
            return true;
        })
        .catch((error) => {
            console.error('Failed to copy to clipboard:', error);
            toast.error('Could not copy to clipboard');
            return false;
        });
}

/**
 * Centralized function to handle sharing across different platforms.
 * Uses react-share under the hood for most platforms and native APIs where appropriate.
 *
 * @param platform - The social platform to share on
 * @param options - Sharing options containing URL and metadata
 * @returns Promise resolving to the result of the sharing operation
 */
export async function shareContent(
    platform: Platform,
    options: ShareOptions,
): Promise<ShareResult> {
    try {
        const { url, title = '', description = '' } = options;
        console.log('url', url);

        // Helper function to open a popup window
        const openSharePopup = (
            shareUrl: string,
            width = 600,
            height = 400,
        ) => {
            const left = (window.innerWidth - width) / 2;
            const top = (window.innerHeight - height) / 2;
            window.open(
                shareUrl,
                'share',
                `width=${width},height=${height},left=${left},top=${top},toolbar=0,menubar=0,location=0,status=0`,
            );
        };

        switch (platform) {
            case 'facebook':
                {
                    // Facebook supports quote parameter for custom text
                    const fbContent = description
                        ? `${title}: ${description}`
                        : title;
                    openSharePopup(
                        `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${encodeURIComponent(fbContent)}`,
                    );
                }
                break;

            case 'twitter':
                {
                    // For Twitter, format the content without including the URL
                    const tweetText = description
                        ? `${title}: ${description}`
                        : title;

                    openSharePopup(
                        `https://twitter.com/intent/tweet?url=${url}&text=${encodeURIComponent(tweetText)}`,
                    );
                }
                break;

            case 'linkedin':
                // LinkedIn uses a different sharing URL format
                openSharePopup(
                    `https://www.linkedin.com/feed/?shareActive=true&shareUrl=${url}`,
                );
                break;

            case 'email':
                try {
                    const subject = `Check out ${title} on ArchiPro`;
                    const body = url;
                    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

                    const emailWindow = window.open(mailtoLink, '_blank');

                    // If window.open returns null, it was likely blocked
                    if (!emailWindow) {
                        throw new Error(
                            'Email client could not be opened. It may have been blocked by your browser.',
                        );
                    }

                    toast.success('Email client opened', {
                        action: {
                            label: 'Dismiss',
                            onClick: () => toast.dismiss(),
                        },
                    });
                } catch (error) {
                    console.error('Failed to open email client:', error);
                    toast.error(
                        'Could not open email client. Try copying the link instead.',
                        {
                            action: {
                                label: 'Copy Link',
                                onClick: () => copyToClipboard(url),
                            },
                        },
                    );
                }
                break;

            case 'copy':
                await copyToClipboard(url);
                break;

            default:
                throw new Error(`Sharing on ${platform} is not implemented`);
        }

        return {
            success: true,
            platform,
        };
    } catch (error) {
        console.error(`Error sharing on ${platform}:`, error);
        return {
            success: false,
            error:
                error instanceof Error
                    ? error.message
                    : 'Unknown error occurred',
            platform,
        };
    }
}

/**
 * Checks if the Web Share API is available in the current browser
 */
export function isNativeShareAvailable(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.share;
}

/**
 * Shares content using the native Web Share API if available
 *
 * @param options - Sharing options containing URL and metadata
 * @returns Promise resolving to the result of the sharing operation
 */
export async function shareNative(options: ShareOptions): Promise<ShareResult> {
    try {
        if (!isNativeShareAvailable()) {
            throw new Error('Native sharing is not available');
        }

        await navigator.share({
            title: options.title,
            text: options.description,
            url: options.url,
        });

        return {
            success: true,
        };
    } catch (error) {
        console.error('Error using native share:', error);
        return {
            success: false,
            error:
                error instanceof Error
                    ? error.message
                    : 'Unknown error occurred',
        };
    }
}
